import { useEffect, useState } from "react";
import { Download, X } from "lucide-react";

const DISMISS_KEY = "bc_pwa_install_dismissed_v1";

/** Captures the beforeinstallprompt event and surfaces a custom dark-luxury
 * install banner on Android/Chrome. iOS Safari does not fire this event —
 * we show a separate "Add to Home Screen" hint instead. */
export default function InstallPrompt() {
  const [deferred, setDeferred] = useState(null);
  const [show, setShow] = useState(false);
  const [iOSHint, setIOSHint] = useState(false);

  useEffect(() => {
    // Already installed?
    if (window.matchMedia("(display-mode: standalone)").matches || window.navigator.standalone) return;
    if (localStorage.getItem(DISMISS_KEY) === "1") return;

    // Android / Chromium path
    const onBefore = (e) => {
      e.preventDefault();
      setDeferred(e);
      setShow(true);
    };
    window.addEventListener("beforeinstallprompt", onBefore);

    // iOS Safari path — fires no event. Detect iOS + Safari + not installed.
    const isIOS = /iPhone|iPad|iPod/.test(navigator.userAgent);
    const isSafari = /^((?!chrome|android|crios|fxios).)*safari/i.test(navigator.userAgent);
    if (isIOS && isSafari) {
      // Delay so it doesn't punch the user the second they land.
      const t = setTimeout(() => setIOSHint(true), 4000);
      return () => {
        clearTimeout(t);
        window.removeEventListener("beforeinstallprompt", onBefore);
      };
    }

    return () => window.removeEventListener("beforeinstallprompt", onBefore);
  }, []);

  const install = async () => {
    if (!deferred) return;
    deferred.prompt();
    const choice = await deferred.userChoice;
    if (choice.outcome !== "accepted") localStorage.setItem(DISMISS_KEY, "1");
    setDeferred(null);
    setShow(false);
  };

  const dismiss = (permanent = true) => {
    if (permanent) localStorage.setItem(DISMISS_KEY, "1");
    setShow(false);
    setIOSHint(false);
  };

  // Android/Chromium install banner
  if (show) {
    return (
      <div data-testid="install-prompt"
        className="fixed bottom-4 left-4 right-4 sm:left-auto sm:right-4 sm:w-96 z-[60] bg-[#0c0c0c] border border-amber-500/40 shadow-2xl p-4">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 bg-amber-500/10 border border-amber-500/30 flex items-center justify-center shrink-0">
            <Download size={18} className="text-amber-500" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-semibold text-white">Install BeatCredsi</div>
            <div className="text-xs text-neutral-400 mt-0.5 leading-relaxed">
              Get the full-screen Music Creator Credit Score app on your phone — no app store needed.
            </div>
            <div className="flex items-center gap-2 mt-3">
              <button onClick={install} data-testid="install-prompt-accept"
                className="px-3 py-2 bg-amber-500 text-black text-xs font-semibold hover:brightness-110">
                Install
              </button>
              <button onClick={() => dismiss(true)} data-testid="install-prompt-dismiss"
                className="text-xs text-neutral-500 hover:text-white font-mono-tech uppercase tracking-widest">
                Not now
              </button>
            </div>
          </div>
          <button onClick={() => dismiss(true)} className="p-1 text-neutral-500 hover:text-white" aria-label="Close">
            <X size={14} />
          </button>
        </div>
      </div>
    );
  }

  // iOS Safari hint
  if (iOSHint) {
    return (
      <div data-testid="install-prompt-ios"
        className="fixed bottom-4 left-4 right-4 z-[60] bg-[#0c0c0c] border border-amber-500/40 shadow-2xl p-4">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 bg-amber-500/10 border border-amber-500/30 flex items-center justify-center shrink-0">
            <Download size={18} className="text-amber-500" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-semibold text-white">Add BeatCredsi to your Home Screen</div>
            <div className="text-xs text-neutral-400 mt-0.5 leading-relaxed">
              Tap the share icon <span className="inline-block px-1 border border-white/20 text-white">↑</span> below, then choose <span className="text-amber-500 font-semibold">Add to Home Screen</span>.
            </div>
            <button onClick={() => dismiss(true)} data-testid="install-prompt-ios-dismiss"
              className="mt-3 text-xs text-neutral-500 hover:text-white font-mono-tech uppercase tracking-widest">
              Got it
            </button>
          </div>
          <button onClick={() => dismiss(true)} className="p-1 text-neutral-500 hover:text-white" aria-label="Close">
            <X size={14} />
          </button>
        </div>
      </div>
    );
  }

  return null;
}
